import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Box,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import AppModal from "../../components/AppModal";
import DeleteIcon from "../../assets/images/delete-icon.svg";
import EditIcon from "../../assets/images/edit-icon.svg";
import { Scrambling } from "../../declarations/interface";
import { useDispatch } from "react-redux";
import { deleteScrambling } from "../../slices/project";
import clsx from "clsx";

export const DEFAULT_SCRAMBLING: Scrambling = {
  description: "",
  selector: "",
  id: "",
  project_id: "",
};

const useStyles = makeStyles((theme) => ({
  body: {
    borderTop: `0.5px solid ${theme.palette.primary40.main}`,
  },
  contentCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  fullWidth: {
    maxWidth: "100%",
  },
}));

const ScramblingContainer: React.FC<{
  list: Scrambling[];
  onEditClick: (scrambling: Scrambling) => void;
}> = ({ list, onEditClick }) => {
  const classes = useStyles();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const dispatch = useDispatch();

  const handleDeleteScrambling = (id: string) => {
    handleDeleteModalOpen();
    setSelectedId(id);
  };

  const handleDeleteModalOpen = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const confirmDelete = () => {
    setDeleteModalOpen(false);
    dispatch(deleteScrambling(selectedId));
  };

  const handleEditClick = (scrambling: Scrambling) => {
    onEditClick(scrambling);
  };

  return (
    <>
      {list.length > 0 && (
        <Box className={classes.body}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: "20%" }} align="left">
                    <Typography variant="h5" color="primary">
                      Description
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: "60%" }} align="left">
                    <Typography variant="h5" color="primary">
                      Selector
                    </Typography>
                  </TableCell>
                  <TableCell style={{ width: "20%" }} align="left">
                    <Typography variant="h5" color="primary">
                      Action
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row, index) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                      <TableCell style={{ width: "20%" }} align="left">
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxWidth="300px"
                        >
                          {row.description}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ maxWidth: "60%" }} align="left">
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxWidth="800px"
                        >
                          {row.selector}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ width: "20%" }} align="left">
                        <Box
                          display={"flex"}
                          justifyContent={"start"}
                          alignItems={"center"}
                          gap={2}
                        >
                          <Tooltip title={"Remove"}>
                            <IconButton
                              sx={{ padding: 0.5 }}
                              onClick={() => {
                                handleDeleteScrambling(row.id);
                              }}
                              data-testid="remove-added-replacement"
                            >
                              <img
                                src={DeleteIcon}
                                alt="close"
                                height="24"
                                width="24"
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Edit"}>
                            <IconButton
                              sx={{ padding: 0.5 }}
                              onClick={() => {
                                handleEditClick(row);
                              }}
                              data-testid="remove-added-replacement"
                            >
                              <img
                                src={EditIcon}
                                alt="close"
                                height="24"
                                width="24"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {list.length === 0 && (
        <Box className={clsx(classes.contentCenter, classes.body)}>
          <Typography variant="h5" color="primary">
            All scrambling selectors added for this project will be visible here
          </Typography>
        </Box>
      )}
      <AppModal
        open={deleteModalOpen}
        onClose={handleDeleteModalClose}
        header={
          <Typography
            variant="h5"
            color="primaryHighlight.main"
            sx={{ fontWeight: 600 }}
          >
            DELETE SELECTOR
          </Typography>
        }
      >
        <Box mb={0.5}>
          <Typography
            variant="h5"
            color="primary"
            sx={{ fontWeight: 400, textAlign: "center" }}
          >
            Are you sure that you want to delete the selector?
          </Typography>
          <Box display="flex" flexDirection="column" gap="8px" mt={2.75}>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                padding: 1.5,
              }}
              onClick={handleDeleteModalClose}
            >
              <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                Cancel
              </Typography>
            </Button>
            <Button
              variant="contained"
              color="error"
              sx={{
                padding: 1.5,
              }}
              onClick={confirmDelete}
              aria-label="Confirm delete"
            >
              <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                Delete selector
              </Typography>
            </Button>
          </Box>
        </Box>
      </AppModal>
    </>
  );
};

export default ScramblingContainer;
